// src/components/adoption/AdoptionPost.jsx
"use client";

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { getUserPlaceholderImage } from '../../utils/imageUtils';
import { getUserTypeColor } from '../../utils/userTypeUtils';
import { getSpeciesName, getBreedName } from '../../utils/petDataUtils';
import { useAuth } from '../../hooks/useAuth';
import { adoptionAPI } from '../../services/adoptionAPI';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';
import MultiMediaDisplay from '../social/posts/MultiMediaDisplay';
import DeleteConfirmationModal from '../modals/DeleteConfirmation';
import AdoptionOfferConfirmationModal from '../modals/AdoptionOfferConfirmationModal';
import './AdoptionPost.css';

const AdoptionPost = ({
  post,
  currentUser,
  onOfferToAdopt,
  onEdit,
  onDelete,
  onViewRequests,
  onViewDetails,
  isHighlighted = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUserType } = useAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showOfferConfirmation, setShowOfferConfirmation] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [hasShared, setHasShared] = useState(post?.has_shared || false);
  const [checkingShareStatus, setCheckingShareStatus] = useState(false);

  const userType = getUserType();
  const userTypeColor = getUserTypeColor(userType);

  // Sync hasShared with post prop if it changes
  useEffect(() => {
    if (typeof post?.has_shared !== 'undefined') {
      setHasShared(post.has_shared);
    }
  }, [post?.has_shared, post?.id]);

  const handleOfferToAdopt = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Send the adoption request first
      await onOfferToAdopt(post.id);
      // Show confirmation modal after successful request
      setShowOfferConfirmation(true);
    } catch (error) {
      console.error('Error sending adoption request:', error);
      // Don't show confirmation modal if request failed
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmOffer = () => {
    // Just close the modal - request was already sent
    setShowOfferConfirmation(false);
  };

  const handleViewProfile = () => {
    navigate(`/social/profile/${post.author.id}`);
  };

  const handleShareToSocial = async () => {
    if (isSharing || hasShared) return;

    setIsSharing(true);
    try {
      await adoptionAPI.shareAdoptionPost(post.id);
      setHasShared(true); // Mark as shared locally
      showSuccessToast(
        t('adoption.post.shareSuccess') ||
        'Adoption post shared to social feed successfully!'
      );
    } catch (error) {
      console.error('Error sharing adoption post:', error);

      // Check if error is due to already shared (409 Conflict)
      if (error?.response?.status === 409) {
        setHasShared(true); // Mark as shared even if error
        showErrorToast(
          t('adoption.post.alreadyShared') ||
          'You have already shared this adoption post.'
        );
      } else {
        showErrorToast(
          t('adoption.post.shareError') ||
          'Failed to share adoption post. Please try again.'
        );
      }
    } finally {
      setIsSharing(false);
    }
  };

  // Helper function to get display value with fallback
  const getDisplayValue = (value, fallback) => {
    return value || (t('adoption.post.pet.notSpecified') || fallback || 'Not specified');
  };

  // Handle single or multiple pets
  const pets = post.pet_info
    ? (Array.isArray(post.pet_info) ? post.pet_info : [post.pet_info])
    : [];
  const primaryPet = pets[0] || {};
  const isAdopted = post.status === 'adopted';
  const isOwner = currentUser?.id === post.author.id;

  // Derive author subtype display (e.g. \"Rescuer\") from post author data
  const formatSubType = (subType) => {
    if (!subType) return null;
    return subType
      .toString()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const authorSubTypeDisplay =
    post.author?.sub_type_display_name ||
    formatSubType(post.author?.sub_type);

  // Check if user has already made a request for this post
  // Backend returns my_request_status: "pending" | "accepted" | "rejected" | null
  const userRequestStatus = post.my_request_status;
  const hasPendingRequest = userRequestStatus === 'pending';

  const cardClassName = [
    'adoption-post-card',
    isAdopted ? 'adopted' : '',
    isHighlighted ? 'adoption-post-card--highlighted' : '',
  ].filter(Boolean).join(' ');

  const petName =
    primaryPet.name ||
    t('adoption.post.pet.unknownName') ||
    'Unnamed pet';

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Calculate color variants for CSS variables
  const colorVariants = {
    main: userTypeColor,
    shadow20: hexToRgba(userTypeColor, 0.2),
    shadow15: hexToRgba(userTypeColor, 0.15),
    shadow25: hexToRgba(userTypeColor, 0.25),
    shadow35: hexToRgba(userTypeColor, 0.35),
    border: userTypeColor,
    bg03: hexToRgba(userTypeColor, 0.03),
    bg05: hexToRgba(userTypeColor, 0.05),
    bg10: hexToRgba(userTypeColor, 0.1),
    hover: userTypeColor, // Will calculate darker shade if needed
  };

  return (
    <div
      className={cardClassName}
      style={{
        '--adoption-post-user-type-color': colorVariants.main,
        '--adoption-post-user-type-shadow-20': colorVariants.shadow20,
        '--adoption-post-user-type-shadow-15': colorVariants.shadow15,
        '--adoption-post-user-type-shadow-25': colorVariants.shadow25,
        '--adoption-post-user-type-shadow-35': colorVariants.shadow35,
        '--adoption-post-user-type-border': colorVariants.border,
        '--adoption-post-user-type-bg-03': colorVariants.bg03,
        '--adoption-post-user-type-bg-05': colorVariants.bg05,
        '--adoption-post-user-type-bg-10': colorVariants.bg10,
      }}
    >
      <div className="adoption-post-card-inner">
        {/* Image Section */}
        <div className="adoption-post-front-image-wrapper">
          <img
            src={
              primaryPet.picture ||
              primaryPet.profile_image_url ||
              primaryPet.profile_image_path ||
              primaryPet.image ||
              primaryPet.avatar ||
              '/images/no-image-pet.png'
            }
            alt={petName}
            onError={(e) => {
              e.target.src = '/images/no-image-pet.png';
            }}
          />

          {/* Owner Actions Overlay */}
          {isOwner && !isAdopted && (
            <div className="adoption-post-owner-actions">
              <button
                type="button"
                className="adoption-post-icon-btn adoption-post-edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit && onEdit(post);
                }}
                title={t('adoption.post.edit') || 'Edit post'}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                </svg>
              </button>
              <button
                type="button"
                className="adoption-post-icon-btn adoption-post-delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(true);
                }}
                title={t('adoption.post.delete') || 'Delete post'}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          )}

          {/* Name Badge */}
          <div className="adoption-post-front-name">
            <span className="adoption-post-front-pet-name">{petName}</span>
          </div>
        </div>

        {/* Content Section */}
        <div className="adoption-post-front-meta">
          <span className={`adoption-post-status-badge ${post.status || 'offered'}`}>
            {isAdopted
              ? t('adoption.post.status.adopted') || 'Adopted'
              : t('adoption.post.status.offered') || 'Offered'}
          </span>
          {authorSubTypeDisplay && (
            <span className="adoption-post-author-subtype">
              {authorSubTypeDisplay}
            </span>
          )}
        </div>

        <div className="adoption-post-front-actions">
          <button
            type="button"
            className="adoption-post-details-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onViewDetails && onViewDetails(post);
            }}
          >
            {t('adoption.post.viewDetails') || 'View Details'}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
          </button>
          <button
            type="button"
            className="adoption-post-share-btn"
            onClick={handleShareToSocial}
            disabled={isSharing || hasShared || checkingShareStatus || isAdopted}
            title={
              hasShared
                ? (t('adoption.post.alreadyShared') || 'Already shared')
                : (t('adoption.post.shareToSocial') || 'Share to social feed')
            }
          >
            {checkingShareStatus ? (
              t('adoption.post.checking') || 'Checking...'
            ) : isSharing ? (
              t('adoption.post.sharing') || 'Sharing...'
            ) : hasShared ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                {t('adoption.post.shared') || 'Shared'}
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
                {t('adoption.post.share') || 'Share'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          onDelete && onDelete(post.id);
          setShowDeleteConfirm(false);
        }}
        title={t('adoption.post.delete') || 'Delete Post'}
        message={t('adoption.post.deleteConfirm') || 'Are you sure you want to delete this adoption post?'}
        confirmText={t('adoption.post.delete') || 'Delete'}
        cancelText={t('common.cancel') || 'Cancel'}
      />

      {/* Adoption Offer Confirmation Modal */}
      <AdoptionOfferConfirmationModal
        isOpen={showOfferConfirmation}
        onClose={() => setShowOfferConfirmation(false)}
        onConfirm={handleConfirmOffer}
        authorName={post.author?.name || post.author?.username}
      />
    </div>
  );
};

export default AdoptionPost;

